import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Slider,
  Stack,
  Typography,
  TextField,
} from '@mui/material';
import { tableToName, getDefaultSettings, getFactoryDefaultSettings } from '../../config';
import { getConfig, postConfig, getDefaultConfig } from '../../services/api';
const SETTINGS_STORAGE_KEY = 'RAG_SETTINGS';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const thresholdToPercent = (value) => Math.round(value * 100);
const percentToThreshold = (pct) => clamp(pct / 100, 0, 1);

const SettingsItem = ({ index, item, allTables, onChange }) => {
  const handleTableChange = (event) => {
    onChange(index, { ...item, table_name: event.target.value });
  };
  const handleThresholdChange = (_, newPercent) => {
    const newThreshold = percentToThreshold(newPercent);
    onChange(index, { ...item, match_threshold: newThreshold });
  };
  const handleCountChange = (_, newCount) => {
    onChange(index, { ...item, match_count: newCount });
  };

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        background: 'linear-gradient(135deg, rgba(102,126,234,0.08) 0%, rgba(118,75,162,0.08) 100%)',
        border: '1px solid rgba(102,126,234,0.25)',
        boxShadow: '0 4px 14px rgba(0,0,0,0.06)',
      }}
    >
      <Stack spacing={2}>
        <FormControl fullWidth size="small">
          <InputLabel id={`table-label-${index}`}>Table</InputLabel>
          <Select
            labelId={`table-label-${index}`}
            label="Table"
            value={item.table_name}
            onChange={handleTableChange}
          >
            {allTables.map((tableKey) => (
              <MenuItem key={tableKey} value={tableKey}>
                {tableToName[tableKey] || tableKey}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Similarity threshold</Typography>
            <Typography variant="caption" color="text.secondary">{thresholdToPercent(item.match_threshold)}%</Typography>
          </Stack>
           
          <Slider
            value={thresholdToPercent(item.match_threshold)}
            onChange={handleThresholdChange}
            min={0}
            max={90}
            step={1}
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `${v}%`}
          />
           <Typography variant="caption" sx={{ fontWeight: 400 }}>60-70% is very strong, 50% is good, 40% is weak  </Typography>
        </Box>

        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Match count</Typography>
            <Typography variant="caption" color="text.secondary">{item.match_count}</Typography>
          </Stack>
          <Slider
            value={item.match_count}
            onChange={handleCountChange}
            min={1}
            max={20}
            step={1}
            valueLabelDisplay="auto"
          />
        </Box>
      </Stack>
    </Box>
  );
};

const SettingsPopover = ({ open, anchorEl, onClose, settings, updateSettings }) => {
  const availableTables = useMemo(() => Object.keys(tableToName || {}), []);
  const [localSettings, setLocalSettings] = useState(settings && settings.length ? settings : getDefaultSettings());
  const [config, setConfig] = useState(null);
  const [contentText, setContentText] = useState('');
  useEffect(() => {
    if (open) {
      // Refresh from props or storage when opened
      const fromProps = Array.isArray(settings) && settings.length ? settings : null;
      const fromStorageRaw = localStorage.getItem(SETTINGS_STORAGE_KEY);
      const fromStorage = fromStorageRaw ? JSON.parse(fromStorageRaw) : null;
      setLocalSettings(fromProps || fromStorage || getDefaultSettings());
      getConfig().then((res) => {
        const data = res.data;
        setConfig(data);
        const promptContent = data?.prompt?.content || '';
        console.log(promptContent);
        setContentText(promptContent);
      }).catch((err) => {
        console.error(err);
      });
    }
  }, [open, settings]);

  const handleItemChange = (index, updated) => {
    setLocalSettings((previousSettings) => {
      const previousItem = previousSettings[index] || {};
      const didMatchCountChange = previousItem.match_count !== updated.match_count;

      // First update the changed item
      let next = previousSettings.map((item, i) => (i === index ? { ...item, ...updated } : item));

      // If match_count changed for any item, apply it to all items
      if (didMatchCountChange && typeof updated.match_count === 'number') {
        next = next.map((item) => ({ ...item, match_count: updated.match_count }));
      }

      return next;
    });
  };

  const handleSave = () => {
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(localSettings));
    updateSettings?.(localSettings);
    // Post full config JSON with only `prompt.content` updated, as raw JSON string
    try {
      const updatedConfig = {
        ...(config || {}),
        prompt: {
          ...(config?.prompt || {}),
          content: contentText,
        },
      };
      const raw = JSON.stringify(updatedConfig);
      postConfig(raw).catch((err) => console.error('Failed to save config', err)).then((res) => {
        window.location.reload();
      });
    } catch (e) {
      console.error('Failed to save config', e);
    }
    onClose?.();
  };

  const handleReset = () => {
    const defaults = getFactoryDefaultSettings();
    setLocalSettings(defaults);
    getDefaultConfig().then((res) => {
      const data = res.data;
      setConfig(data);
      const promptContent = data?.prompt?.content || '';
      setContentText(promptContent);
    }).catch((err) => {
      console.error(err);
    });
  };

  return (
    <Popover
      open={Boolean(open && anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={{ zIndex: 15000 }}
      PaperProps={{
        sx: {
          width: { xs: 360, sm: 460 },
          maxWidth: '92vw',
          maxHeight: '75vh',
          overflow: 'hidden',
          borderRadius: 3,
          background: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(102,126,234,0.25)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15), 0 8px 24px rgba(102,126,234,0.15)',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={{ p: 2, borderBottom: '1px solid rgba(102,126,234,0.2)', flexShrink: 0 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Settings
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Configure tables, similarity, and match count
        </Typography>
      </Box>

      <Box sx={{ p: 2, overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            background: 'linear-gradient(135deg, rgba(102,126,234,0.08) 0%, rgba(118,75,162,0.08) 100%)',
            border: '1px solid rgba(102,126,234,0.25)',
            boxShadow: '0 4px 14px rgba(0,0,0,0.06)'
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Assistant system prompt
          </Typography>
          <TextField
            value={contentText}
            onChange={(e) => setContentText(e.target.value)}
            placeholder="Edit the assistant's system instructions shown to users"
            multiline
            minRows={8}
            maxRows={18}
            fullWidth
            variant="outlined"
            InputProps={{
              sx: {
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                background: 'rgba(255,255,255,0.9)'
              }
            }}
          />
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.75 }}>
            Changes made here will be saved to the server but may be overwritten. Use for temporary testing only.
          </Typography>
        </Box>
        {(localSettings || []).map((item, idx) => (
          <Box key={idx}>
            <SettingsItem index={idx} item={item} allTables={availableTables} onChange={handleItemChange} />
            {idx !== localSettings.length - 1 && <Divider sx={{ my: 1.5 }} />}
          </Box>
        ))}
      </Box>

      <Box sx={{ p: 2, borderTop: '1px solid rgba(102,126,234,0.2)', display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button variant="text" color="inherit" onClick={handleReset}>Reset</Button>
        <Button variant="contained" onClick={handleSave}>Save</Button>
      </Box>
    </Popover>
  );
};

export default SettingsPopover;


